import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, Stack, Button, CircularProgress, Typography, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import { useField } from '../_data_sources/useField';
import useManageFields from '../_data_sources/useManageFields';
import Modal from 'components/_layouts/Modal';
import datetimeHelper from 'helpers/datetimeHelper';
import DetailList from '_lex/_atoms/DetailList';
import { useQueryClient } from 'react-query';
import { ActionMenu } from 'components/_atoms';
import { ServerErrorResponse } from 'data_sources/constants';
import { useConfirm } from 'material-ui-confirm';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with UTC support
dayjs.extend(utc);

export default function Overview() {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { id } = useParams();
  const fieldQuery = useField(Number(id), true);

  // Helper function to format default value based on field type
  const formatDefaultValue = (defaultValue: string | null | undefined, fieldType: string): string => {
    if (!defaultValue) return '--';

    switch (fieldType) {
      case 'Time':
        // For time fields, show only the time portion in user's local timezone
        const timeValue = dayjs.utc(defaultValue).local();
        return timeValue.isValid() ? timeValue.format('HH:mm') : defaultValue;

      case 'Date':
        const dateValue = dayjs.utc(defaultValue);
        return dateValue.isValid() ? dateValue.format('MM/DD/YYYY') : defaultValue;

      case 'Boolean':
        // For boolean fields, show True/False instead of true/false strings
        if (defaultValue === 'true') return 'True';
        if (defaultValue === 'false') return 'False';
        return defaultValue;

      default:
        // For other field types (Text, Number), show as-is
        return defaultValue;
    }
  };
  const { publish, deactivate } = useManageFields();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [deactivateChangeDescription, setDeactivateChangeDescription] = useState('');
  const confirm = useConfirm();

  const canManageFieldBank = hasPermission('manageFieldBank', currentOrganizationId || '');

  const handleEdit = () => {
    // Navigate to the edit page
    navigate(`../${fieldQuery.data?.id}/edit`);
  };

  const handlePublish = async () => {
    if (!fieldQuery.data?.id) return;

    setIsPublishing(true);
    try {
      await publish(
        fieldQuery.data.id,
        'Published field',
        () => {
          enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.publishSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          // Fields list first, then individual field
          queryClient.invalidateQueries(['fields']);
          queryClient.invalidateQueries(['field', fieldQuery.data?.id, true]);
          queryClient.invalidateQueries(['fieldAuditLog', fieldQuery.data?.id]);
        },
        (error: ServerErrorResponse) => {
          // Extract specific error message from backend if available
          let errorMessage = t('performanceReporting.fieldBank.details.actions.publishError');

          if (error?.errorDetails && error.errorDetails.length > 0) {
            // Use the first error detail message if available
            errorMessage = error.errorDetails[0].message || error.errorDetails[0].detail || errorMessage;
          } else if (error?.errorMessage) {
            // Fallback to general error message from backend
            errorMessage = error.errorMessage;
          }

          enqueueSnackbar(errorMessage, {
            variant: 'error',
            autoHideDuration: 5000,
          });
        }
      );
    } catch (error: any) {
      // Extract specific error message from backend if available
      let errorMessage = t('performanceReporting.fieldBank.details.actions.publishError');

      if (error?.errorDetails && error.errorDetails.length > 0) {
        // Use the first error detail message if available
        errorMessage = error.errorDetails[0].message || error.errorDetails[0].detail || errorMessage;
      } else if (error?.errorMessage) {
        // Fallback to general error message from backend
        errorMessage = error.errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDeactivate = async () => {
    if (!fieldQuery.data?.id) return;

    // Show change log modal for deactivation
    setDeactivateChangeDescription('');
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!fieldQuery.data?.id || !deactivateChangeDescription.trim()) return;

    setIsDeactivating(true);
    try {
      await deactivate(
        fieldQuery.data.id,
        deactivateChangeDescription.trim(),
        () => {
          enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          // Fields list first, then individual field
          queryClient.invalidateQueries(['fields']);
          queryClient.invalidateQueries(['field', fieldQuery.data?.id, true]);
          queryClient.invalidateQueries(['fieldAuditLog', fieldQuery.data?.id]);
          setIsDeactivating(false);
          setIsDeactivateModalOpen(false);
          setDeactivateChangeDescription('');
        },
        (error: ServerErrorResponse) => {
          enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateError'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
          setIsDeactivating(false);
        }
      );
    } catch (error: any) {
      console.error('Error deactivating field:', error);
      enqueueSnackbar(t('performanceReporting.fieldBank.details.actions.deactivateError'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      setIsDeactivating(false);
    }
  };

  const handleDeactivateCancel = () => {
    setIsDeactivateModalOpen(false);
    setDeactivateChangeDescription('');
  };

  // Build action menu items based on field status and permissions
  const actionMenuItems = React.useMemo(() => {
    if (!canManageFieldBank) return [];

    // Don't show actions for inactive fields
    if (fieldQuery.data?.status?.toLowerCase() === 'inactive') return [];

    const items = [
      {
        key: 'edit',
        label: t('performanceReporting.fieldBank.details.actions.edit'),
        onClick: isPublishing || isDeactivating ? undefined : handleEdit,
      },
    ];

    // Add conditional items based on status
    if (fieldQuery.data?.status?.toLowerCase() === 'draft') {
      items.push({
        key: 'publish',
        label: isPublishing
          ? t('performanceReporting.fieldBank.details.actions.publishing')
          : t('performanceReporting.fieldBank.details.actions.publish'),
        onClick: isPublishing ? undefined : handlePublish,
      });
    }

    if (fieldQuery.data?.status?.toLowerCase() === 'active') {
      items.push({
        key: 'deactivate',
        label: isDeactivating
          ? t('performanceReporting.fieldBank.details.actions.deactivating')
          : t('performanceReporting.fieldBank.details.actions.deactivate'),
        onClick: isDeactivating ? undefined : handleDeactivate,
      });
    }

    return items;
  }, [canManageFieldBank, fieldQuery.data?.status, isPublishing, isDeactivating, t]);

  const formattedDefaultValue = formatDefaultValue(fieldQuery.data?.defaultValue, fieldQuery.data?.fieldType || '');
  const shouldTruncateValue = (value?: string | null) => {
    if (!value) return false;
    return value.length > 200 || value.includes('\n');
  };

  const getDefaultValueDisplay = () => {
    if (!fieldQuery.data) return formattedDefaultValue;

    if (fieldQuery.data.fieldType === 'Time' && fieldQuery.data.defaultValue) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return (
        <>
          {formattedDefaultValue}
          <Typography
            component='div'
            variant='caption'
            sx={{ color: 'text.secondary', mt: 0.5 }}
          >
            Time displayed in {userTimezone} timezone
          </Typography>
        </>
      );
    }

    return formattedDefaultValue;
  };

  return (
    <Stack spacing={12}>
      <Card className='lex-card'>
        <CardHeader
          title={t('performanceReporting.fieldBank.details.title')}
          action={
            canManageFieldBank && actionMenuItems.length > 0 ? (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
              >
                {(isPublishing || isDeactivating) && <CircularProgress size={20} />}
                <ActionMenu
                  id='field-details-actions'
                  btnText={t('performanceReporting.fieldBank.details.actions.title')}
                  btnProps={{
                    disabled: isPublishing || isDeactivating,
                  }}
                  items={actionMenuItems}
                />
              </Stack>
            ) : null
          }
        />
        <CardContent>
          <DetailList
            items={
              fieldQuery.data && fieldQuery.data.name
                ? [
                    {
                      label: t('performanceReporting.fieldBank.details.properties.name'),
                      value: fieldQuery.data?.name,
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.fieldType'),
                      value: t(`performanceReporting.fieldBank.fieldType.${fieldQuery.data?.fieldType}`),
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.updatedAt'),
                      value: datetimeHelper.formatDateTime(fieldQuery.data?.updatedAt),
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.createdAt'),
                      value: datetimeHelper.formatDateTime(fieldQuery.data?.createdAt),
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.fieldCategory'),
                      value: fieldQuery.data?.fieldCategory?.name,
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.defaultValue'),
                      value: getDefaultValueDisplay(),
                      truncate: shouldTruncateValue(formattedDefaultValue),
                      truncateLines: 4,
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.description'),
                      value: fieldQuery.data?.description,
                      truncate: shouldTruncateValue(fieldQuery.data?.description),
                      truncateLines: 4,
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.label'),
                      value: fieldQuery.data?.label,
                    },
                    {
                      label: t('performanceReporting.fieldBank.details.properties.eisScoreable'),
                      value: fieldQuery.data?.isEisScorable ? 'True' : 'False',
                    },
                  ]
                : []
            }
          />
        </CardContent>
      </Card>

      {/* Deactivation Change Log Modal */}
      <Modal
        open={isDeactivateModalOpen}
        onClose={handleDeactivateCancel}
        title={t('performanceReporting.fieldBank.details.deactivateConfirm.title')}
        size='sm'
        actions={
          <>
            <Button
              color='neutral'
              onClick={handleDeactivateCancel}
              disabled={isDeactivating}
            >
              {t('performanceReporting.fieldBank.details.deactivateConfirm.cancel')}
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={handleDeactivateConfirm}
              disabled={!deactivateChangeDescription.trim() || isDeactivating}
              loading={isDeactivating}
              loadingPosition='end'
            >
              {t('performanceReporting.fieldBank.details.deactivateConfirm.confirm')}
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <Typography
            variant='body1'
            sx={{ pt: 4 }}
          >
            {t('performanceReporting.fieldBank.details.deactivateConfirm.message')}
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            id='deactivateChangeDescription'
            label={t('performanceReporting.fieldBank.modal.noteLabel')}
            type='text'
            fullWidth
            variant='outlined'
            value={deactivateChangeDescription}
            onChange={(e) => setDeactivateChangeDescription(e.target.value)}
            error={false}
            multiline
            minRows={2}
          />
        </Stack>
      </Modal>
    </Stack>
  );
}
